import { useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import { toastError, toastSuccess } from '@/stores'
import { useQuoteBuilderProStore } from '@/stores/quote-builder-pro-store'
import { downloadClientPdf, quoteProToClientPdf } from '@/modules/client-delivery/pdf'

const QUERY_KEY = ['quote-builder-pro'] as const

export function useQuoteBuilderPro() {
  const hydrate = useQuoteBuilderProStore((s) => s.hydrate)
  const ready = useQuoteBuilderProStore((s) => s.ready)
  const document = useQuoteBuilderProStore((s) => s.document)
  const category = useQuoteBuilderProStore((s) => s.category)
  const query = useQuoteBuilderProStore((s) => s.query)
  const results = useQuoteBuilderProStore((s) => s.results)
  const templates = useQuoteBuilderProStore((s) => s.templates)
  const history = useQuoteBuilderProStore((s) => s.history)
  const newDocument = useQuoteBuilderProStore((s) => s.newDocument)
  const setTitle = useQuoteBuilderProStore((s) => s.setTitle)
  const setCategory = useQuoteBuilderProStore((s) => s.setCategory)
  const setQuery = useQuoteBuilderProStore((s) => s.setQuery)
  const search = useQuoteBuilderProStore((s) => s.search)
  const addComparisonRow = useQuoteBuilderProStore((s) => s.addComparisonRow)
  const addFromComparisonSelection = useQuoteBuilderProStore((s) => s.addFromComparisonSelection)
  const addService = useQuoteBuilderProStore((s) => s.addService)
  const applyTemplate = useQuoteBuilderProStore((s) => s.applyTemplate)
  const updateBlock = useQuoteBuilderProStore((s) => s.updateBlock)
  const removeBlock = useQuoteBuilderProStore((s) => s.removeBlock)
  const duplicateBlock = useQuoteBuilderProStore((s) => s.duplicateBlock)
  const moveBlock = useQuoteBuilderProStore((s) => s.moveBlock)
  const setGlobalMargin = useQuoteBuilderProStore((s) => s.setGlobalMargin)
  const setGlobalDiscount = useQuoteBuilderProStore((s) => s.setGlobalDiscount)
  const saveStore = useQuoteBuilderProStore((s) => s.save)
  const load = useQuoteBuilderProStore((s) => s.load)

  const bootstrap = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      hydrate()
      return true
    },
  })

  useEffect(() => {
    if (!ready) hydrate()
  }, [ready, hydrate])

  const save = useCallback(() => {
    try {
      saveStore()
      toastSuccess('Orçamento salvo', 'Autosave + histórico atualizados')
    } catch (error) {
      toastError('Salvar', error instanceof Error ? error.message : 'Falha ao salvar')
    }
  }, [saveStore])

  const exportPdf = useCallback(() => {
    try {
      saveStore()
      const current = useQuoteBuilderProStore.getState().document
      void downloadClientPdf(quoteProToClientPdf(current)).then((result) => {
        toastSuccess('PDF gerado com sucesso', result.filename)
      })
    } catch (error) {
      toastError('PDF', error instanceof Error ? error.message : 'Falha ao gerar PDF')
    }
  }, [saveStore])

  return {
    ready,
    document,
    category,
    query,
    results,
    templates,
    history,
    isLoading: bootstrap.isLoading,
    newDocument,
    setTitle,
    setCategory,
    setQuery,
    search,
    addComparisonRow,
    addFromComparisonSelection,
    addService,
    applyTemplate,
    updateBlock,
    removeBlock,
    duplicateBlock,
    moveBlock,
    setGlobalMargin,
    setGlobalDiscount,
    save,
    load,
    exportPdf,
  }
}
